import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo, useCallback } from 'react';
import { SkeletonLoader } from '@widget/loaders';
import { getFontAwesomeIcon, hasValue } from '@utils';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
export function isObjectInfoCardProps(data) {
    return data && typeof data === 'object' && 'fields' in data && Array.isArray(data.fields);
}
const ObjectInfoCardBase = ({ fields, columns = 2, layout = 'vertical', valueAlign, className = '', loading, title }) => {
    const effectiveValueAlign = valueAlign !== null && valueAlign !== void 0 ? valueAlign : (layout === 'vertical' ? 'start' : 'end');
    const renderFieldValue = useCallback((field) => {
        if (field === null || field === void 0 ? void 0 : field.renderValue) {
            return field.renderValue(field === null || field === void 0 ? void 0 : field.value);
        }
        if ((field === null || field === void 0 ? void 0 : field.value) === null || (field === null || field === void 0 ? void 0 : field.value) === undefined) {
            return 'N/A';
        }
        if (typeof (field === null || field === void 0 ? void 0 : field.value) === 'object' && React.isValidElement(field === null || field === void 0 ? void 0 : field.value)) {
            return field === null || field === void 0 ? void 0 : field.value;
        }
        return String(field === null || field === void 0 ? void 0 : field.value);
    }, []);
    function renderField(field, index) {
        return (_jsxs("div", { className: `uxpcore_obsdpgd__field uxpcore_obsdpgd__field--${layout}`, children: [_jsx("div", { className: "uxpcore_obsdpgd__field_label", children: loading
                        ? _jsx(SkeletonLoader, { width: '4rem', height: '1rem' })
                        : _jsxs(_Fragment, { children: [hasValue(field.icon) && (_jsx("div", { className: "uxpcore_obsdpgd__field_label-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(field.icon) }) })), _jsx("div", { className: "uxpcore_obsdpgd__field_label-text", children: field.label }), hasValue(field.label)] }) }), hasValue(field.label) && layout === 'horizontal' && _jsx("span", { children: ":" }), _jsx("div", { className: `uxpcore_obsdpgd__field_value uxpcore_obsdpgd__field_value--align-${effectiveValueAlign}`, children: loading
                        ? _jsx(SkeletonLoader, { width: '4rem', height: '1rem' })
                        : renderFieldValue(field) })] }, index));
    }
    if (columns > 1) {
        const cols = Array.from({ length: columns }, (_, c) => fields.filter((_, i) => i % columns === c));
        return (_jsxs("div", { className: `uxpcore_obsdpgd__container ${className}`, children: [title && _jsx("div", { className: "uxpcore_obsdpgd__container_title", children: title }), _jsx("div", { className: "uxpcore_obsdpgd__columns-wrapper", children: cols.map((colFields, c) => (_jsx("div", { className: "uxpcore_obsdpgd__column", children: colFields.map((field, i) => renderField(field, i)) }, c))) })] }));
    }
    return (_jsxs("div", { className: `uxpcore_obsdpgd__container ${className}`, children: [title ? (_jsx("div", { className: "uxpcore_obsdpgd__container_title", children: title })) : null, _jsx("div", { className: `uxpcore_obsdpgd__grid uxpcore_obsdpgd__grid--columns-${columns}`, children: fields === null || fields === void 0 ? void 0 : fields.map((field, index) => renderField(field, index)) })] }));
};
/**
 * @export
 * A component that displays a grid of labeled fields with optional icons and custom value rendering.
 *
 * @example
 * ```
 * <ObjectInfoCard
 *   fields={[
 *     { label: 'Name', value: 'John Doe' },
 *     { label: 'Email', value: 'john@example.com' }
 *   ]}
 * />
 * ```
 *
 * @example
 * ```
 * <ObjectInfoCard
 *   fields={[
 *     { label: 'Name', value: 'John Doe', icon: 'user' },
 *     { label: 'Email', value: 'john@example.com', icon: 'envelope', renderValue: (value) => <a href={`mailto:${value}`}>{value}</a> },
 *     { label: 'Status', value: 'Active' }
 *   ]}
 *   columns={3}
 *   className="custom-info-card"
 * />
 * ```
 */
export const ObjectInfoCard = memo(ObjectInfoCardBase);
